/*
 * <event-plan-picker> — plan + quantity purchase UI for a multi-day event.
 *
 * The shopper picks a plan (a single-day product or the 両日 bundle), a
 * participant count, and each participant's 参加区分. On book we add one cart
 * line per participant (identical variants merge to a single qty-N line;
 * mixed 参加区分 stay as separate lines) and hand off to Shopify's native
 * checkout. No personal data is collected here. Prices are pre-formatted
 * strings from Liquid's money filter; totals parse the digits (JPY).
 */
if (!customElements.get('event-plan-picker')) {
  customElements.define(
    'event-plan-picker',
    class EventPlanPicker extends HTMLElement {
      connectedCallback() {
        this.cards = Array.from(this.querySelectorAll('.event-plan__card'));
        if (!this.cards.length) return;
        this.plans = this.cards.map((card) => ({
          el: card,
          note: card.dataset.note || '',
          variants: JSON.parse(card.querySelector('.event-plan__data').textContent),
        }));
        this.kubuns = Object.keys(this.plans[0].variants);
        // A date list is sorted chronologically, so the first card is not
        // necessarily the date this page is about.
        this.plan = this.plans.find((p) => p.el.dataset.initial === 'true') || this.plans[0];
        this.ctaLabel = this.dataset.ctaLabel || '予約する';
        this.attendeeHeading = this.dataset.attendeeHeading || '参加者';
        this.attendees = [{ kubun: this.kubuns[0] }];

        this.countSel = this.querySelector('[data-count]');
        this.countSel.addEventListener('change', () => {
          const n = parseInt(this.countSel.value, 10) || 1;
          while (this.attendees.length < n) this.attendees.push({ kubun: this.kubuns[0] });
          this.attendees.length = n;
          this.renderAttendees();
          this.renderSummary();
        });
        this.plans.forEach((p) =>
          p.el.addEventListener('click', (e) => {
            e.preventDefault();
            this.plan = p;
            this.renderSummary();
          })
        );
        // Optional add-on: a fixed variant of a second event, one per
        // attendee. It is a plain extra cart line, not a bundle, so each
        // event keeps its own SKU downstream.
        this.addon = this.querySelector('[data-addon]');
        // The wrapper, not the <label> — it carries the variant id and price,
        // and the label cannot enclose the details link without every click
        // on that link toggling the box.
        this.addonHost = this.querySelector('.event-plan__addon-wrap');
        if (this.addon) this.addon.addEventListener('change', () => this.renderSummary());

        this.querySelector('[data-add]').addEventListener('click', () => this.add());

        this.renderAttendees();
        this.renderSummary();
      }

      amount(priceStr) {
        return parseInt(String(priceStr).replace(/[^0-9]/g, ''), 10) || 0;
      }

      renderAttendees() {
        const wrap = this.querySelector('[data-attendees]');
        // A single participant with no per-person choice needs no block —
        // the top-level plan + one 参加区分 is enough. Only show per-person
        // 参加区分 rows when there's more than one participant.
        if (this.attendees.length < 2) {
          wrap.innerHTML = '';
          this.singleKubun = true;
          this.renderSingleKubun();
          return;
        }
        this.singleKubun = false;
        this.querySelector('[data-single-kubun]').innerHTML = '';
        wrap.innerHTML = '';
        this.attendees.forEach((a, i) => {
          const row = document.createElement('div');
          row.className = 'event-plan__attendee-row';
          const label = document.createElement('span');
          label.className = 'event-plan__attendee-label';
          label.textContent = `${this.attendeeHeading} ${i + 1}`;
          row.appendChild(label);
          row.appendChild(this.kubunButtons(a));
          wrap.appendChild(row);
        });
      }

      renderSingleKubun() {
        const host = this.querySelector('[data-single-kubun]');
        host.innerHTML = '';
        host.appendChild(this.kubunButtons(this.attendees[0]));
      }

      kubunButtons(a) {
        const kwrap = document.createElement('div');
        kwrap.className = 'event-plan__kubun';
        this.kubuns.forEach((k) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.textContent = k;
          b.className = 'event-plan__kubun-btn' + (a.kubun === k ? ' is-active' : '');
          b.addEventListener('click', () => {
            a.kubun = k;
            if (this.singleKubun) this.renderSingleKubun();
            else this.renderAttendees();
            this.renderSummary();
          });
          kwrap.appendChild(b);
        });
        return kwrap;
      }

      renderSummary() {
        this.plans.forEach((p) => p.el.classList.toggle('is-selected', p === this.plan));
        let total = 0;
        let anyUnavailable = false;
        this.attendees.forEach((a) => {
          const v = this.plan.variants[a.kubun];
          if (v) {
            total += this.amount(v.price);
            if (!v.available) anyUnavailable = true;
          }
        });
        if (this.addonChecked()) {
          total += this.amount(this.addonHost.dataset.addonPrice) * this.attendees.length;
        }
        this.querySelector('[data-total]').textContent = '¥' + total.toLocaleString();
        this.querySelector('[data-seatnote]').textContent = this.plan.note || '';
        const add = this.querySelector('[data-add]');
        add.disabled = anyUnavailable;
        add.textContent = anyUnavailable ? '売り切れ' : this.ctaLabel;
      }

      addonChecked() {
        return !!(this.addon && this.addon.checked && this.addonHost);
      }

      add() {
        const err = this.querySelector('[data-error]');
        err.hidden = true;
        // One line per attendee, kept apart by _seat. Two people on the same
        // 参加区分 would otherwise merge into a single line of quantity 2,
        // leaving the 申込書 nowhere to hang the second person's answers — and
        // the CRM filing one row with 人数 2 instead of a person each.
        const items = [];
        this.attendees.forEach((a, i) => {
          const v = this.plan.variants[a.kubun];
          if (!v) return;
          items.push({ id: v.id, quantity: 1, properties: { _seat: String(i + 1) } });
        });
        if (!items.length) return;
        if (this.addonChecked()) {
          // Everyone in the booking attends the second event too — same seats,
          // so the same _seat, which lets the form offer "same as above".
          const addonId = Number(this.addonHost.dataset.addonId);
          this.attendees.forEach((a, i) => {
            items.push({ id: addonId, quantity: 1, properties: { _seat: String(i + 1) } });
          });
        }

        const btn = this.querySelector('[data-add]');
        btn.disabled = true;
        // The picker states the whole booking, and the 申込書 that follows is
        // that booking. Adding to whatever was already there lets a second
        // press quietly double it, so each press starts from an empty cart.
        fetch('/cart/clear.js', { method: 'POST' })
          .then(() =>
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        }))
          .then((r) => (r.ok ? r.json() : r.json().then((j) => Promise.reject(j))))
          .then(() => {
            if (this.dataset.cartRedirect === 'true') window.location.href = '/cart';
            else {
              btn.disabled = false;
              btn.textContent = 'カートに追加しました ✓';
            }
          })
          .catch((j) => {
            btn.disabled = false;
            err.hidden = false;
            err.textContent = (j && j.description) || 'カートに追加できませんでした。';
          });
      }
    }
  );
}
